import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import { eventIsOver } from '~shared/components/sport-event/utils';
import mainSocket from '~shared/services/mainSocketClass';
import { useLazyLoadContext } from '~spa/Components/LazyLoad/LazyLoad';
import {
    getFeaturedInplayEvents,
    formatInplayWidgetWsParams,
    updateEvents,
    updatePrices,
    updatePricesGameLine,
} from './utils';
import InplayWidget from './InplayWidget';

const InplayWidgetContainer = ({
    sport = null,
    showFooter = false,
    source = null,
}) => {
    const { lazyLoadHasLoaded, lazyLoadIsEmpty } = useLazyLoadContext();
    const [loaded, setLoaded] = useState(false);
    const [eventsData, setEventsData] = useState({});
    const unsubscribeFunction = useRef(Function);

    useEffect(() => {
        getFeaturedInplayEvents(sport).then(data => {
            const { events: wsEvents = [] } = (data ?? {});

            if(wsEvents.length) {
                setEventsData(data);
                setLoaded(true);

                lazyLoadHasLoaded();
            } else {
                lazyLoadIsEmpty();
            }
        });
    }, []);

    useEffect(() => {
        if (loaded) {
            const wsParams = formatInplayWidgetWsParams(eventsData?.events);

            mainSocket.subscribeForUpdates(wsParams, 'InplayWidget');
            unsubscribeFunction.current = mainSocket.onWsEventUpdate(wsMessageHandler);
        }

        return () => {
            mainSocket.unsubscribeForUpdates(formatInplayWidgetWsParams(eventsData?.events));
            unsubscribeFunction.current();
        };
    }, [loaded]);

    const wsMessageHandler = ({
        action = '',
        event: wsEvent = null,
        events: wsEvents = [],
    }) => {
        if (action === 'inplay_update' || action === 'price_update') {
            setEventsData(eventsData => {
                const newEvent = wsEvents[0] || wsEvent;
                const {
                    id: eventId = null,
                    markets: newMarkets = [],
                    match_status: matchStatus,
                } = newEvent;
                const {
                    events = [],
                    markets = {},
                    selections = {},
                } = eventsData;
                const currentEvent = events.find(item => item.id === eventId);

                if (currentEvent) {
                    const {
                        usesThreeMarketView = false,
                    } = currentEvent;
                    let updatedEvents = events;
                    let updatedMarkets = markets;
                    let updatedSelections = selections;

                    if (eventIsOver(matchStatus)) {
                        unsubscribeFunction.current(formatInplayWidgetWsParams([currentEvent]));
                        updatedEvents = events.filter(event => event.id !== eventId);
                        updatedSelections = omit(selections, eventId);

                        return {
                            ...eventsData,
                            events: updatedEvents,
                            selections: updatedSelections,
                        };
                    }

                    updatedEvents = updateEvents(newEvent, eventsData);

                    if (newMarkets.length) {
                        if (usesThreeMarketView) {
                            const {
                                gameLineMarkets,
                                gameLineSelections,
                            } = updatePricesGameLine(newEvent, {
                                event: currentEvent,
                                markets,
                                selections,
                            });

                            updatedMarkets = gameLineMarkets;
                            updatedSelections = gameLineSelections;
                        } else {
                            updatedSelections = updatePrices(newEvent, {
                                event: currentEvent,
                                selections,
                            });
                        }
                    }

                    return {
                        ...eventsData,
                        events: updatedEvents,
                        markets: updatedMarkets,
                        selections: updatedSelections,
                    };
                }

                return eventsData;
            });
        }
    };

    return (<>
    {
        Object.keys(eventsData?.selections ?? []).length
            ? <InplayWidget
                events={eventsData.events}
                markets={eventsData.markets}
                markets_headers={eventsData.markets_headers}
                selections={eventsData.selections}
                showFooter={showFooter}
                source={source}
            />
            : null
    }
    </>);
};

InplayWidgetContainer.propTypes = {
    sport: PropTypes.string,
    showFooter: PropTypes.bool,
    source: PropTypes.string,
};

export default InplayWidgetContainer;
